import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const PAY_STATUS = {
  pending:   { label: 'Pendente',   color: '#b7791f' },
  approved:  { label: 'Aprovado',   color: '#276749' },
  rejected:  { label: 'Rejeitado',  color: '#c0392b' },
  cancelled: { label: 'Cancelado',  color: '#718096' },
};

const LABEL_STATUS = {
  pending:              { label: 'Aguardando',         color: '#718096' },
  processing:           { label: 'Processando',        color: '#3182ce' },
  generated:            { label: 'Gerada',             color: '#276749' },
  failed:               { label: 'Falha',              color: '#c0392b' },
  insufficient_balance: { label: 'Saldo insuficiente', color: '#b7791f' },
};

const badge = (color) => ({
  background: color + '22',
  color,
  border: `1px solid ${color}55`,
  borderRadius: 20,
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  display: 'inline-block',
});

const formatDate = (iso) => new Date(iso).toLocaleString('pt-BR');
const formatBrl  = (n)   => `R$ ${parseFloat(n || 0).toFixed(2).replace('.', ',')}`;

export default function Orders() {
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selected, setSelected]       = useState(null);
  const [actionLoading, setActionLoading] = useState('');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/orders');
      setOrders(res.data);
    } catch {
      alert('Erro ao carregar pedidos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleCheckPayment = async (order) => {
    setActionLoading(order._id + '-check');
    try {
      const { data } = await api.post(`/admin/orders/${order._id}/check-payment`);
      if (data.approved) {
        setOrders(prev => prev.map(o =>
          o._id === order._id ? { ...o, paymentStatus: 'approved', labelStatus: data.labelTriggered ? 'processing' : o.labelStatus } : o
        ));
        if (selected?._id === order._id)
          setSelected(s => ({ ...s, paymentStatus: 'approved' }));
        alert(data.labelTriggered
          ? 'Pagamento confirmado no Mercado Pago! A geração da etiqueta foi disparada. Atualize em alguns segundos.'
          : 'Pagamento confirmado no Mercado Pago!');
      } else {
        const nomes = { pending: 'pendente', rejected: 'rejeitado', not_found: 'não encontrado', in_process: 'em análise' };
        alert(`Ainda não há pagamento aprovado para este pedido (status no Mercado Pago: ${nomes[data.status] || data.status}).`);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao verificar pagamento.');
    } finally {
      setActionLoading('');
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(
      `Excluir permanentemente o pedido de ${order.customer?.name || 'cliente'} (${formatBrl(order.totalAmount)})?\n\nUse para limpar tentativas de pagamento abandonadas.`
    )) return;

    setActionLoading(order._id + '-delete');
    try {
      await api.delete(`/admin/orders/${order._id}`);
      setOrders(prev => prev.filter(o => o._id !== order._id));
      if (selected?._id === order._id) setSelected(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir pedido.');
    } finally {
      setActionLoading('');
    }
  };

  const handleGenerateLabel = async (order) => {
    setActionLoading(order._id + '-label');
    try {
      const res = await api.post(`/admin/orders/${order._id}/generate-label`);
      const patch = res.data.success
        ? { labelStatus: 'generated', labelUrl: res.data.labelUrl }
        : { labelStatus: res.data.reason || 'failed', labelError: res.data.error };

      setOrders(prev => prev.map(o => o._id === order._id ? { ...o, ...patch } : o));
      if (selected?._id === order._id) setSelected(s => ({ ...s, ...patch }));

      if (res.data.success)
        alert('Etiqueta gerada com sucesso! Clique em "Imprimir" para abrir.');
      else
        alert(`Falha: ${res.data.reason}${res.data.error ? '\n' + res.data.error : ''}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao gerar etiqueta.');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Pedidos</h1>
        <button className="btn-admin-secondary" onClick={fetchOrders} disabled={loading}>
          {loading ? 'Carregando...' : 'Atualizar'}
        </button>
      </div>

      {loading && <p style={{ color: '#999' }}>Carregando pedidos...</p>}
      {!loading && orders.length === 0 && <div className="empty-state">Nenhum pedido encontrado.</div>}

      {!loading && orders.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={TH}>Data</th>
                <th style={TH}>Cliente</th>
                <th style={TH}>Total</th>
                <th style={TH}>Tipo</th>
                <th style={TH}>Pagamento</th>
                <th style={TH}>Etiqueta</th>
                <th style={TH}></th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => {
                const ps = PAY_STATUS[order.paymentStatus]   || PAY_STATUS.pending;
                const ls = LABEL_STATUS[order.labelStatus]   || LABEL_STATUS.pending;
                const busyCheck  = actionLoading === order._id + '-check';
                const busyLabel  = actionLoading === order._id + '-label';
                const busyDelete = actionLoading === order._id + '-delete';
                const canGenerate = order.paymentStatus === 'approved'
                  && ['pending', 'failed', 'insufficient_balance'].includes(order.labelStatus);

                return (
                  <React.Fragment key={order._id}>
                    <tr
                      style={{
                        borderBottom: selected?._id === order._id ? 'none' : '1px solid #eee',
                        cursor: 'pointer',
                        background: selected?._id === order._id ? '#f0f7ff' : 'white',
                      }}
                      onClick={() => setSelected(s => s?._id === order._id ? null : order)}
                    >
                      <td style={TD}>{formatDate(order.createdAt)}</td>
                      <td style={TD}>
                        <span style={{ fontWeight: 600 }}>{order.customer?.name || '—'}</span>
                        <br />
                        <small style={{ color: '#666' }}>{order.customer?.email}</small>
                      </td>
                      <td style={TD}>{formatBrl(order.totalAmount)}</td>
                      <td style={TD}>{order.paymentType === 'pix' ? 'PIX' : 'Cartão/Boleto'}</td>
                      <td style={TD}><span style={badge(ps.color)}>{ps.label}</span></td>
                      <td style={TD}><span style={badge(ls.color)}>{ls.label}</span></td>
                      <td style={{ ...TD, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        {order.paymentStatus === 'pending' && (
                          <>
                            <button
                              className="btn-admin-sm-blue"
                              disabled={busyCheck}
                              onClick={() => handleCheckPayment(order)}
                              title="Consultar o status real do pagamento no Mercado Pago"
                            >
                              {busyCheck ? '...' : '🔄 Verificar pagamento'}
                            </button>
                            <button
                              className="btn-admin-sm-red"
                              disabled={busyDelete}
                              onClick={() => handleDeleteOrder(order)}
                              title="Excluir pedido abandonado"
                              style={{ marginLeft: 4 }}
                            >
                              {busyDelete ? '...' : '🗑 Excluir'}
                            </button>
                          </>
                        )}
                        {canGenerate && (
                          <button
                            className="btn-admin-sm-blue"
                            disabled={busyLabel}
                            onClick={() => handleGenerateLabel(order)}
                            style={{ marginLeft: 4 }}
                          >
                            {busyLabel ? 'Gerando...' : '🏷 Gerar Etiqueta'}
                          </button>
                        )}
                        {order.labelUrl && (
                          <a
                            href={order.labelUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-admin-sm-green"
                            style={{ textDecoration: 'none', marginLeft: 4 }}
                            onClick={e => e.stopPropagation()}
                          >
                            🖨 Imprimir
                          </a>
                        )}
                      </td>
                    </tr>

                    {/* ── Detalhe inline quando selecionado ── */}
                    {selected?._id === order._id && (
                      <tr style={{ background: '#f0f7ff', borderBottom: '2px solid #bee3f8' }}>
                        <td colSpan={7} style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                            <div>
                              <strong style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>CLIENTE</strong>
                              {order.customer?.name}<br />
                              <small>{order.customer?.email}</small><br />
                              <small>{order.customer?.phone}</small>
                            </div>
                            <div>
                              <strong style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>ENTREGA</strong>
                              <small>
                                {order.delivery?.street}, {order.delivery?.number}
                                {order.delivery?.complement ? ` — ${order.delivery.complement}` : ''}<br />
                                {order.delivery?.district} · {order.delivery?.city}/{order.delivery?.state}<br />
                                CEP {order.delivery?.postalCode}
                              </small>
                            </div>
                            <div>
                              <strong style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>FRETE</strong>
                              <small>
                                {order.shippingOption?.name || order.shippingOption?.service_description || '—'}<br />
                                {formatBrl(order.shippingAmount)}<br />
                                ID ME: <code>{order.shippingServiceId || '—'}</code>
                              </small>
                            </div>
                            <div>
                              <strong style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>ITENS</strong>
                              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                                {(order.items || []).map((item, i) => (
                                  <li key={i}>{item.title} x{item.quantity} — {formatBrl(item.price)}</li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          {order.labelError && (
                            <div style={{ marginTop: 10, background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#c0392b' }}>
                              <strong>Erro na etiqueta:</strong> {order.labelError}
                            </div>
                          )}
                          {order.labelUrl && (
                            <div style={{ marginTop: 10 }}>
                              <a href={order.labelUrl} target="_blank" rel="noreferrer" style={{ color: '#276749', fontWeight: 600, fontSize: 13 }}>
                                🖨 Abrir / Imprimir Etiqueta
                              </a>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

const TH = { padding: '8px 10px', fontWeight: 600, textAlign: 'left', color: '#555', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' };
const TD = { padding: '10px 10px', verticalAlign: 'middle', fontSize: 13 };
